import React, { useCallback, useMemo, useState } from 'react';
import { StatsCard } from 'common/components';
import { removeLegacyDraftById, useLegacyDraftMigration } from 'features/drafts';
import { useShowAlert } from 'hooks';
import useDatatable, {
  buildRowActionsRendererDefinition,
  extractObjectFromDataTable,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import DataTable from 'UI/components/organisms/DataTable';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import { RowActions } from 'UI/constants/defaults';

export const DraftsSelector = ({
  columns,
  endpoint,
  entity,
  entityLabel,
  onSelectDraft,
  orderByOptions,
  pageKey
}) => {
  const initialPreferences = useMemo(
    () => getColumnPreferences(pageKey, 0, orderByOptions, columns),
    [columns, orderByOptions, pageKey]
  );
  const { showAlert } = useShowAlert();
  const { isMigratingLegacyDrafts } = useLegacyDraftMigration({ entity });
  const [uiState, setUiState] = useState({
    isDeleteDialogOpen: false,
    isDeleting: false,
    draft: null
  });

  const paramsExtender = useCallback(() => ({ entityType: entity }), [entity]);

  const {
    columnPreferences,
    count,
    data,
    getData,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePerPageChange,
    listState
  } = useDatatable({
    key: pageKey,
    apiInstance: SECONDARY_API_CLIENT,
    columnsDefinitions: columns,
    enableFetchData: !isMigratingLegacyDrafts,
    endpoint,
    entityName: `${entity}-drafts`,
    initialPreferences,
    orderByOptions,
    paramsExtender,
    shouldScrollOnNavigate: true
  });

  const { isLoading, showWholeSkeleton, page, perPage, keyword } = listState;
  const { columns: finalColumns, columnOrder, orderBy, direction } = columnPreferences;

  const handleSelectDraft = async ({ rowData }) => {
    if (!rowData || !onSelectDraft) return;

    const item = extractObjectFromDataTable(columns, ['id'], rowData);
    item && onSelectDraft(item);
  };

  const handleOpenDeleteDialog = ({ rowData }) => {
    if (!rowData) return;
    const item = extractObjectFromDataTable(columns, ['id', 'legacyId'], rowData);

    setUiState(prev => ({
      ...prev,
      isDeleteDialogOpen: true,
      draft: item
    }));
  };

  const handleDeleteDraft = async () => {
    try {
      const selectedDraftId = uiState.draft?.id;
      if (!selectedDraftId) return;

      setUiState(prev => ({
        ...prev,
        isDeleting: true
      }));

      await SECONDARY_API_CLIENT.delete(`${endpoint}/${selectedDraftId}`);

      showAlert({
        title: strings.drafts.alert.success.delete.title,
        autoHideDuration: 5000
      });

      setUiState(prev => ({
        ...prev,
        isDeleteDialogOpen: false,
        isDeleting: false,
        draft: null
      }));

      if (uiState.draft?.legacyId) {
        removeLegacyDraftById({ draftId: uiState.draft?.legacyId, entity });
      }

      await getData();
    } catch (error) {
      setUiState(prev => ({
        ...prev,
        isDeleting: false
      }));

      showAlert({
        title: strings.shared.ui.error,
        error
      });
    }
  };

  const actionsButton = buildRowActionsRendererDefinition({
    actions: [
      { name: RowActions.Edit, onClick: handleSelectDraft },
      { name: RowActions.Delete, onClick: handleOpenDeleteDialog }
    ],
    columns
  });

  const columnsToRender = getColumnsToRender([...finalColumns, actionsButton]);

  return (
    <StatsCard header={strings.drafts.cardTitle} paddingContent={0}>
      <DataTable
        columnOrder={columnOrder?.length > 0 ? columnOrder : undefined}
        columns={columnsToRender}
        components={{ TableToolbar: () => null }}
        count={count ?? 0}
        data={data}
        draggableColumns={{ enabled: true }}
        elevation={0}
        loading={showWholeSkeleton || isMigratingLegacyDrafts}
        onColumnDisplayClick={handleColumnDisplayChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnSortChange={handleColumnSortChange}
        onPageClick={handlePageChange}
        onPerPageClick={handlePerPageChange}
        onSearchTextChange={handleKeywordChange}
        onToggleFilters={handleFiltersToggle}
        page={page}
        refreshing={isLoading || isMigratingLegacyDrafts}
        rowsPerPage={perPage}
        searchText={keyword}
        selectToolbarPlacement="none"
        selectableRows="none"
        sortOrder={{ name: orderBy, direction }}
        subtitle={strings.drafts.emptyState.subtitle}
        title={strings.drafts.emptyState.title}
      />

      <When condition={uiState.isDeleteDialogOpen}>
        <DecisionDialog
          fetching={uiState.isDeleting}
          isOpened
          message={strings.formatString(strings.drafts.confirmDialog.delete.message, {
            entity: entityLabel ?? entity
          })}
          onClose={() => setUiState(prev => ({ ...prev, isDeleteDialogOpen: false }))}
          onConfirm={handleDeleteDraft}
          showSeverityIcon
          title={strings.drafts.confirmDialog.delete.title}
          withButtons="YesNo"
        />
      </When>
    </StatsCard>
  );
};
